import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  TextField,
  Button
} from '@mui/material'
import { useEffect, useRef, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty, fullReportLimit } from '../../utils/utils'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen,
  LoadingDialog
} from '../../utils/ui_utils'
import moment from 'moment'
import { BarChart } from '../../components/Chart'
import { OutletAndDatePickerUI } from '../../utils/ui_components/outletAndDatePickerUi.js'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import {
  useDownloadReports,
  useFetchCounterWiseReport,
  useFetchItemwiseReport,
  useFetchOutletSummary
} from '../../modules/reports/reportsHooks'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import CounterwiseReportDetails from './reportComponents/counterwiseReportDetails.js'
import { generateXLSXCSV } from '../../utils/tsUtils/utils.js'
import { getUserType } from '../../components/tsComponents/AuthGuard'

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const CounterwiseReport = () => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  // New states for all data, graph, and table
  const [allCounterData, setAllCounterData] = useState([])
  const [graphData, setGraphData] = useState({
    labels: [moment().format('MMM-DD')],
    datasets: [
      {
        label: 'Total Sales',
        data: [0],
        backgroundColor: ['#ffbb11']
      }
    ]
  })
  const [tableData, setTableData] = useState([])
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [reportVisibility, setReportVisibility] = useState(false)
  const [goToPageInput, setGoToPageInput] = useState('')
  const [openDailog, setOpenDailog] = useState(false)
  const [counterDetails, setCounterDetails] = useState([])
  // Option A: guard so we pull the full record set only once per search
  // (prevents an infinite refetch loop). Reset on every new report request.
  const fullFetchRef = useRef(false)
  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()
  const {
    outletSummaryData,
    outletSummaryError,
    outletSummaryFetch,
    outletSummaryLoading
  } = useFetchOutletSummary()
  const {
    counterwiseReportData,
    counterwiseReportError,
    counterwiseReportFetch,
    counterwiseReportLoading
  } = useFetchCounterWiseReport()
  const { itemwiseReportFetch } = useFetchItemwiseReport()

  const { downloadReportsLoading, downloadReportsFetch } = useDownloadReports()

  // Determine user type and build Owner-only 'All Outlets' option
  const userType = getUserType()
  const isOwner = userType === 'Owner'
  const baseOutlets = Array.isArray(outletFetchData?.data)
    ? outletFetchData.data.filter(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
    : []
  const outletsWithAll = isOwner
    ? [{ _id: '0', name: 'All Outlets' }, ...baseOutlets]
    : baseOutlets

  // '0' means All Outlets only for Owner
  const isAllOutletsSelected = isOwner && selectedOutlet === '0'

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const validateAndGetReport = (page) => {
    let tempFromDate = moment(fromDate)
    let tempToDate = moment(toDate)
    if (tempFromDate.isAfter(tempToDate)) {
      return
    }
    // For non-owners, require a specific outlet
    if (!isOwner && isEmpty(selectedOutlet)) {
      return
    }
    // Always fetch reports and summary
    fullFetchRef.current = false
    fetchRecord(page)
    fetchRecordSummary()
  }

  const fetchRecord = (page, fetchLimit = 1000) => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: 1,
      limit: fetchLimit // First request probes; if the range has more records
      // than this, the effect below refetches with the full count (Option A).
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    counterwiseReportFetch(payload)
  }

  const fetchRecordSummary = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD')
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    outletSummaryFetch(payload)
  }

  const fetchUserWiseReportOnError = () => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit
    }

    // Only include outlet_id if a specific outlet is selected
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    counterwiseReportFetch(payload)
  }

  const fetchOutletOnError = () => {
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    // Client-side pagination: update tableData from allCounterData
    const startIdx = (value - 1) * limit
    const endIdx = startIdx + limit
    setTableData(allCounterData.slice(startIdx, endIdx))
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Counters',
          align: 'start',
          font: {
            weight: 'bold',
            style: 'italic'
          }
        },
        ticks: {
          maxRotation: 35,
          minRotation: 35,
          autoSkip: true,
          autoSkipPadding: 10,
          maxTicksLimit: 20,
          font: {
            size: 9
          },
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value)
            // Truncate long labels
            if (label && label.length > 15) {
              return label.substring(0, 12) + '...'
            }
            return label
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 10
          }
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Sales Trend',
        padding: {
          top: 10,
          bottom: 30
        }
      },
      legend: {
        display: true,
        position: 'bottom'
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            // Show full counter name in tooltip
            return context[0].label
          }
        }
      },
      datalabels: {
        display: function (context) {
          // Only show datalabels if there are less than 15 bars
          return context.chart.data.labels.length < 15
        },
        color: 'black',
        formatter: Math.round,
        anchor: 'end',
        offset: -20,
        align: 'start',
        font: {
          size: 9
        }
      }
    }
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }

  const history = useNavigate()

  useEffect(() => {
    businessCheck(history)
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }, [])

  useEffect(() => {
    // Default selection based on role after outlets load
    if (isOwner) {
      setSelectedOutlet('0')
    } else if (
      Array.isArray(outletFetchData?.data) &&
      outletFetchData.data.length > 0
    ) {
      setSelectedOutlet(String(outletFetchData.data[0]._id))
    } else {
      setSelectedOutlet('')
    }
  }, [outletFetchData, isOwner])

  useEffect(() => {
    // Option A: the list endpoint is paginated. If the selected range has more
    // records than we just received, refetch once with the full count so the
    // graph, the table and the summary "Total" row all reflect every record.
    // Without this, a wide range is silently truncated and the graph/records
    // disagree with the summary total.
    const totalRecords = counterwiseReportData?.total || 0
    const fetchedRecords = counterwiseReportData?.data?.length || 0
    if (
      fetchedRecords > 0 &&
      totalRecords > fetchedRecords &&
      !fullFetchRef.current
    ) {
      fullFetchRef.current = true
      const limit = fullReportLimit(totalRecords)
      if (limit < totalRecords) {
        setPopUpMessage(
          `Showing the first ${limit} of ${totalRecords} records. Narrow the date range to see all of them.`
        )
        setSeverity('warning')
        setSnackBar(true)
      }
      fetchRecord(1, limit)
      return
    }

    if (counterwiseReportData.data?.length > 0) {
      // Store all data in state
      setAllCounterData(counterwiseReportData.data)

      // Set graph data with all data
      setGraphData({
        labels: counterwiseReportData.data.map(
          (data) => data['KitchenCounterName']
        ),
        datasets: [
          {
            label: 'Total Sales',
            data: counterwiseReportData.data.map((data) => data['TotalAmount']),
            backgroundColor: ['#e0218a'],
            barThickness: 30
          }
        ]
      })

      // Set table data for current page
      const startIdx = (page - 1) * limit
      const endIdx = startIdx + limit
      setTableData(counterwiseReportData.data.slice(startIdx, endIdx))

      pageCount(counterwiseReportData.data.length)
      setReportVisibility(true)
    } else {
      setAllCounterData([])
      setGraphData([])
      setTableData([])
      setReportVisibility(false)
    }
  }, [counterwiseReportData, page, limit])

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const getCounterwiseReportDetails = (counter) => {
    let payload = {
      from_date: moment(fromDate).format('YYYY-MM-DD'),
      to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
      kitchen_counter_id: counter.KitchenCounterId,
      pageNumber: 1,
      limit: 15
    }
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }
    itemwiseReportFetch(payload)
    setCounterDetails(counter)
    setOpenDailog(true)
  }

  const refreshFunction = () => validateAndGetReport(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } =
    useReportRefresh(refreshFunction, [page, selectedOutlet, fromDate, toDate])

  useEffect(() => {
    setPage(1)
    validateAndGetReport(1)
  }, [selectedOutlet, fromDate, toDate])

  const exportToCsv = async () => {
    try {
      let payload = {
        from_date: moment(fromDate).format('YYYY-MM-DD'),
        to_date: moment(toDate).add(1, 'days').format('YYYY-MM-DD'),
        pageNumber: 1,
        limit: 100,
        total: counterwiseReportData?.total || 15,
        type: 'counterwise'
      }

      if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
        payload.outlet_id = selectedOutlet
      }

      const reports = await downloadReportsFetch(payload)

      let data1 = [...reports.payload]

      if (outletSummaryData && outletSummaryData.length > 0) {
        data1.push(
          {},
          {
            Bill_Date: 'Total Amount',
            Bill_Num: outletSummaryData[0].Total_Bills,
            Total_GST: outletSummaryData[0]?.Total_GST?.toFixed(2),
            Total_Cess: outletSummaryData[0].Total_Cess?.toFixed(2),
            Total_Disc: outletSummaryData[0].Total_Disc?.toFixed(2),
            Total: outletSummaryData[0].Total_Sales?.toFixed(2)
          }
        )
      }

      const options = {
        keys: [
          { field: 'KitchenCounterName', title: 'Counter Name' },
          { field: 'NoOfQTY', title: 'Quantity' },
          { field: 'TotalGST', title: 'GST' },
          { field: 'TotalCESS', title: 'Cess' },
          { field: 'TotalDiscount', title: 'Discount' },
          { field: 'TotalAmount', title: 'Total' }
        ]
      }

      let outletName = 'All_Outlets'
      if (!isAllOutletsSelected) {
        const selectedOutletData = outletFetchData.data.find(
          (outlet) => String(outlet._id) === String(selectedOutlet)
        )
        outletName = selectedOutletData?.name || 'Unknown_Outlet'
      }

      let filename = `CounteWiseReport_${outletName}`
      filename += `_${payload.from_date}_to_${payload.to_date}`

      const csv = await generateXLSXCSV(data1, options.keys, filename)

      toastMessage('CSV Downloaded Successfully')
    } catch (error) {
      console.error('Error while converting JSON to CSV', error)
      toastMessage('Error While Downloading csv', 'error')
    }
  }

  return (
    <>
      <Helmet title="Counterwise Report" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Counterwise Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={counterwiseReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 2 }} />
      {outletFetchError ? (
        <ErrorScreen
          onClick={() => {
            fetchOutletOnError()
          }}
        />
      ) : (
        <div>
          <OutletAndDatePickerUI
            outlets={outletsWithAll}
            onOutletChange={handleOutletChange}
            selectedOutlet={selectedOutlet}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={(newValue) => {
              newValue.setHours(0, 0, 0)
              setFromDate(newValue)
              if (moment(toDate).isBefore(newValue)) {
                setToDateErrorMessage('To Date is earler than From Date')
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            onFromDateError={(reason, value) => {
              if (reason) {
                setErrorMessage(reason)
                setShowError(true)
              } else {
                setErrorMessage('')
                setShowError(false)
              }
            }}
            onToDateChange={(newValue) => {
              newValue.setHours(0, 0, 0)
              setToDate(newValue)
              if (moment(fromDate).isAfter(newValue)) {
                setToDateErrorMessage('To Date is earler than From Dates')
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            onToDateError={(reason, value) => {
              if (reason) {
                setToDateErrorMessage(reason)
                setShowToDateError(true)
              } else {
                setToDateErrorMessage('')
                setShowToDateError(false)
              }
            }}
            showFromDateError={showError}
            showToDateError={showToDateError}
            fromDateErrorMessage={errorMessage}
            toDateErrorMessage={toDateErrorMessage}
            exportComponent={
              counterwiseReportData.data &&
              counterwiseReportData.data.length > 0 && (
                <Tooltip title="Export to CSV" placement="top" arrow>
                  <IconButton
                    variant="contained"
                    onClick={exportToCsv}
                    disabled={downloadReportsLoading}
                    size="large"
                  >
                    {downloadReportsLoading && <CircularProgress size={20} />}
                    {!downloadReportsLoading ? (
                      <>
                        {' '}
                        <CloudUploadIcon fontSize="large" />
                      </>
                    ) : (
                      <> </>
                    )}
                  </IconButton>
                </Tooltip>
              )
            }
          />
          {counterwiseReportError ? (
            <ErrorScreen
              onClick={() => {
                fetchUserWiseReportOnError()
              }}
            />
          ) : (
            <div>
              {(counterwiseReportLoading == true ||
                counterwiseReportLoading == null ||
                outletFetchLoading) && (
                <Grid
                  container
                  spacing={10}
                  direction={'column'}
                  sx={{ paddingTop: 5 }}
                >
                  <Grid item>
                    <Grid
                      container
                      rowSpacing={4.5}
                      columnSpacing={10}
                      sx={{ mt: 1 }}
                    >
                      <Grid item xs sm md={8}>
                        <MainCard content={false} sx={{ mt: 1.5 }}>
                          <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                            <ListTypeShimmer />
                          </Box>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {!counterwiseReportError &&
                !outletFetchLoading &&
                !outletFetchError &&
                !counterwiseReportLoading &&
                reportVisibility && (
                  <Grid
                    container
                    spacing={10}
                    direction={'column'}
                    sx={{ paddingTop: 5 }}
                  >
                    <Grid item>
                      <Grid
                        container
                        rowSpacing={4.5}
                        columnSpacing={10}
                        sx={{ mt: 1 }}
                      >
                        <Grid item xs={12}>
                          <Typography variant="h5" gutterBottom>
                            Sales Trend
                          </Typography>
                          <MainCard
                            content={false}
                            sx={{
                              mt: 1.5,
                              width: '100%',
                              maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                              '@media (max-width: 960px)': {
                                maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                              }
                            }}
                          >
                            <Box
                              sx={{
                                pt: 1,
                                pr: 1,
                                pl: 1,
                                pb: 1,
                                width: '100%',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                height: {
                                  xs: '300px',
                                  sm: '350px',
                                  md: '400px'
                                }
                              }}
                            >
                              <BarChart
                                chartData={graphData}
                                options={chartOptions}
                              />
                            </Box>
                          </MainCard>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MainCard
                            content={false}
                            sx={{
                              width: '100%',
                              maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                              '@media (max-width: 960px)': {
                                maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <TableContainer
                                component={Card}
                                sx={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  overflow: 'auto'
                                }}
                              >
                                <Table size="small">
                                  <TableHead size="small">
                                    <StyledTableRow>
                                      <StyledTableCell align="left">
                                        {' '}
                                        Counter Name
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        No Of Qty
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        GST
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        CESS
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Discount
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Amount
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  </TableHead>
                                  {outletSummaryData &&
                                    outletSummaryData.length > 0 && (
                                      <TableBody>
                                        <TableRow
                                          sx={{
                                            backgroundColor: '#ECF9FF'
                                          }}
                                        >
                                          <StyledTableCell
                                            align="left"
                                            sx={{ fontWeight: 'bold' }}
                                          >
                                            TOTAL:
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            -
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0].Total_GST?.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0].Total_Cess?.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0].Total_Disc?.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                          <StyledTableCell
                                            sx={{ fontWeight: 'bold' }}
                                            align="right"
                                          >
                                            {outletSummaryData[0].Total_Sales?.toFixed(
                                              2
                                            )}
                                          </StyledTableCell>
                                        </TableRow>
                                      </TableBody>
                                    )}
                                  <TableBody>
                                    {tableData.map((row, i) => (
                                      <StyledTableRow
                                        key={i}
                                        hover
                                        onClick={() =>
                                          getCounterwiseReportDetails(row)
                                        }
                                      >
                                        <StyledTableCell align="left">
                                          {row?.KitchenCounterName}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row?.NoOfQTY}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row?.TotalGST.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row?.TotalCESS.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row?.TotalDiscount.toFixed(2)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row?.TotalAmount.toFixed(2)}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </CardContent>
                            {pagecount > 1 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                  p: 2,
                                  backgroundColor: 'grey.50',
                                  borderTop: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%'
                                  }}
                                >
                                  <Pagination
                                    count={pagecount}
                                    page={page}
                                    color="secondary"
                                    size="small"
                                    boundaryCount={2}
                                    siblingCount={0}
                                    showFirstButton
                                    showLastButton
                                    onChange={handlePageChange}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        transition: 'all 0.2s ease-in-out'
                                      }
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    backgroundColor: 'white',
                                    p: 2,
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    transition: 'box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500
                                    }}
                                  >
                                    Jump to
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={goToPageInput}
                                    onChange={handleGoToPageChange}
                                    onKeyPress={handleGoToPageKeyPress}
                                    placeholder="Page"
                                    sx={{
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        transition: 'all 0.2s ease-in-out',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                          borderWidth: '1px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '1.5px'
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '2px',
                                          boxShadow:
                                            '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                        }
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        padding: '7px 8px',
                                        height: '20px',
                                        lineHeight: '20px'
                                      }
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 400
                                    }}
                                  >
                                    of {pagecount}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={handleGoToPageSubmit}
                                    disabled={
                                      !goToPageInput ||
                                      parseInt(goToPageInput, 10) < 1 ||
                                      parseInt(goToPageInput, 10) > pagecount
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 500,
                                      px: 2,
                                      py: 0.75,
                                      height: '34px',
                                      lineHeight: 1,
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow:
                                          '0 4px 8px rgba(156, 39, 176, 0.3)'
                                      },
                                      '&:active': {
                                        transform: 'translateY(0)'
                                      }
                                    }}
                                  >
                                    Go
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </MainCard>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              {!reportVisibility &&
                !counterwiseReportLoading &&
                !counterwiseReportError &&
                !outletFetchLoading &&
                !outletFetchError && <ShowNoRecordsFound />}
            </div>
          )}
        </div>
      )}
      <CounterwiseReportDetails
        visibility={openDailog}
        onClose={() => setOpenDailog(false)}
        counter={counterDetails}
        selectedOutlet={selectedOutlet}
        fromDate={moment(fromDate).format('YYYY-MM-DD')}
        toDate={moment(toDate).add(1, 'days').format('YYYY-MM-DD')}
      />
      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>
      <LoadingDialog open={downloadReportsLoading} />
    </>
  )
}

export default CounterwiseReport
